import React, { useState } from 'react';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
} from '../../../../../shared/globalStyling/styledText';
import { formatWholeNumber } from '../../../../../../core/utils/campaigns';
import uuid from 'uuid';
import SimpleTable from '../../../../../shared/SimpleTable';

const SegmentsTable = ({ segmentsData }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Process data for duplicate "Open Targeting" and palce it last in the list
  const openTargetingSegments = [];
  const otherSegments = [];
  segmentsData.forEach((segment) => {
    if (segment.audience_id === 'Open Targeting' || segment.audience_id === 'N/A') {
      openTargetingSegments.push(segment);
    } else {
      otherSegments.push(segment);
    }
  });
  const mergedSegmentsData = [];
  if (openTargetingSegments.length > 1) {
    const mergedOpenTargeting = openTargetingSegments.slice(1).reduce(
      (acc, curr) => {
        for (const key of ['clicks', 'ais']) {
          acc[key] += curr[key];
        }
        return acc;
      },
      { ...openTargetingSegments[0] }
    );
    mergedSegmentsData.push(...otherSegments, mergedOpenTargeting);
  } else if (openTargetingSegments.length === 1) {
    mergedSegmentsData.push(...otherSegments, openTargetingSegments[0]);
  } else {
    mergedSegmentsData.push(...segmentsData);
  }

  // Filter out "Open Targeting" rows when impressions, clicks, and CTR are all 0
  const filteredSegmentsData = mergedSegmentsData
    .map((segments) => {
      return {
        ...segments,
        key: `${segments.id}${uuid.v4()}`,
        retargeting_id: segments.retargeting_id,
        audience_id:
          segments.audience_id === null ? segments.retargeting_name : segments.audience_id,
        ctr: segments.ais ? (segments.clicks / segments.ais) * 100 : 0,
      };
    })
    .filter((segment) => {
      // Hide "Open Targeting" rows when impressions, clicks, and CTR are all 0
      if (
        (segment.audience_id === 'Open Targeting' || segment.audience_id === 'N/A') &&
        segment.ais === 0 &&
        segment.clicks === 0
      ) {
        return false;
      }
      return true;
    });

  const columns = [
    {
      title: <TableColumnTitle text={'Audience Targeting'} />,
      dataIndex: mergedSegmentsData.retargeting_name ? 'retargeting_name' : 'audience_id',
      key: mergedSegmentsData.retargeting_name ? 'retargeting_name' : 'audience_id',
      width: '250px',
      align: 'left',
      sorter: (a, b) => {
        // Make it so that "Open Targeting" is always at the end of the list
        // and sort the rest of the data normally
        if (
          a.retargeting_name === 'Open Targeting' ||
          a.audience_id === 'Open Targeting' ||
          b.retargeting_name === 'Open Targeting' ||
          b.audience_id === 'Open Targeting'
        ) {
          return 0;
        }
        return a.retargeting_name.localeCompare(b.retargeting_name);
      },
      render: (retargeting_name) => {
        return <PrimaryTableRowText text={retargeting_name || 'Audience Name Not Available'} />;
      },
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'ais',
      footerSum: true,
      key: 'ais',
      width: '130px',
      align: 'left',
      sorter: (a, b) => {
        // Make it so that "Open Targeting" is always at the end of the list
        // and sort the rest of the data normally
        if (
          a.retargeting_name === 'Open Targeting' ||
          a.audience_id === 'Open Targeting' ||
          b.retargeting_name === 'Open Targeting' ||
          b.audience_id === 'Open Targeting'
        ) {
          return 0;
        }
        return a.ais - b.ais;
      },
      render: (ais) => <PrimaryTableRowText text={ais && formatWholeNumber(ais)} />,
    },
    {
      title: <TableColumnTitle text={'Clicks'} />,
      footerSum: true,
      dataIndex: 'clicks',
      key: 'clicks',
      width: '100px',
      align: 'left',
      sorter: (a, b) => {
        // Make it so that "Open Targeting" is always at the end of the list
        // and sort the rest of the data normally
        if (
          a.retargeting_name === 'Open Targeting' ||
          a.audience_id === 'Open Targeting' ||
          b.retargeting_name === 'Open Targeting' ||
          b.audience_id === 'Open Targeting'
        ) {
          return 0;
        }
        return a.clicks - b.clicks;
      },
      render: (clicks) => <PrimaryTableRowText text={clicks && formatWholeNumber(clicks)} />,
    },
    {
      title: <TableColumnTitle text={'CTR'} />,
      footerContent: '-',
      dataIndex: 'ctr',
      key: 'ctr',
      width: '80px',
      align: 'left',
      sorter: (a, b) => {
        // Make it so that "Open Targeting" is always at the end of the list
        // and sort the rest of the data normally
        if (
          a.retargeting_name === 'Open Targeting' ||
          a.audience_id === 'Open Targeting' ||
          b.retargeting_name === 'Open Targeting' ||
          b.audience_id === 'Open Targeting'
        ) {
          return 0;
        }
        return a.ctr - b.ctr;
      },
      render: (ctr) => <PrimaryTableRowText text={ctr && `${ctr.toFixed(3)}%`} />,
    },
  ];

  return (
    <SimpleTable
      footer={
        filteredSegmentsData && filteredSegmentsData.slice(currentPage * 10 - 10, currentPage * 10)
      }
      bordered={false}
      columns={columns}
      data={filteredSegmentsData}
      pagination={{
        pageSize: 10,
        total: filteredSegmentsData && filteredSegmentsData.length,
        current: currentPage,
      }}
      scroll={{ x: 'max-content' }}
      onChange={(paginationObject) => {
        setCurrentPage(paginationObject.current);
      }}
    />
  );
};

export default SegmentsTable;
